import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Method,
    Prop,
    State,
    Watch,
} from "@stencil/core";

import { Column, TableRow, TableRowWithId } from "./table-interfaces";

import { debounce } from "../../utils/debounce";
import applyElementPolyfills from "../../polyfills/element";
import { SortData, SortDirection } from "./table-types";

applyElementPolyfills();

@Component({
    tag: "uic-table",
    styleUrl: "table.scss",
    shadow: false,
})
export class Table {
    /** the table should always be at least 100% wide */
    @Prop() fullWidth: boolean = false;
    /** the object containing the table data coming from the prop */
    @Prop() data?: TableRow[];
    /** this enables the self-sorting feature for the table. If you display incomplete data (i.e. with pagination) you should do the sorting by yourself using the uicTableSort events */
    @Prop() selfSorting: boolean = false;
    /** defines the initial sorting criteria which should be applied once the data is loaded */
    @Prop() initialSortingCriterion?: SortData;
    /** if set, the table will lazy render the rows */
    @Prop() lazyRender: boolean = false;
    /** (for lazy-render) target element to wich the scroll listener will be attached */
    @Prop() scrollEventTarget: string = "";
    /** will be taken as row height for lazy loading calculations */
    @Prop() lazyRowHeight: number = 67;
    /** if set, the thead of the table will be set to a sticky position */
    @Prop() stickyThead: boolean = false;
    /** contains the data of the columns to be rendered */
    @State() tableColumns: Column[] = [];
    /** contains the data of the columns to be rendered */
    @State() tableColumnsMethods: any[] = [];
    /** contains the ids of the selected rows */
    @State() selectedRows: string[] = [];
    /** data in the final sorted order */
    @State() sortedData: TableRowWithId[] = [];

    private scrollEventTargetElement: Window | HTMLElement =
        this.scrollEventTarget === ""
            ? window
            : (document.querySelector(this.scrollEventTarget) as HTMLElement);

    @Element() element: HTMLUicTableElement;

    private baseClass = "uic-table";
    private thead: HTMLElement = null;
    private tableInner: HTMLDivElement = null;

    private readonly debouncedUpdateTableColumns = debounce(
        () => this.updateTableColumns(),
        100
    );

    /** content observer to watch for attribute changes in table column components  */
    private contentObserver: MutationObserver = new MutationObserver(
        this.debouncedUpdateTableColumns
    );

    componentWillLoad() {
        this.updateTableColumns().then(() => this.onDataChanged());
    }

    componentDidLoad() {
        this.contentObserver.observe(this.element, {
            childList: true,
            subtree: true,
            attributes: true,
        });
        if (this.lazyRender) {
            this.initTable();
        }
    }

    connectedCallback() {
        if (this.lazyRender && !this.stickyThead) {
            if (this.scrollEventTargetElement !== null) {
                this.scrollEventTargetElement.addEventListener(
                    "scroll",
                    this.onScroll,
                    {
                        passive: true,
                    }
                );
            }
        }
    }

    disconnectedCallback() {
        this.contentObserver.disconnect();
        if (this.lazyRender && !this.stickyThead) {
            if (this.scrollEventTargetElement !== null) {
                this.scrollEventTargetElement.removeEventListener(
                    "scroll",
                    this.onScroll
                );
            }
        }
    }

    /** updates the tableColumn state by getting the props of the uic-table-columns */
    private async updateTableColumns() {
        const children = Array.from(this.element.children as any);
        this.tableColumns = children.filter(
            (child: HTMLUicTableColumnElement) =>
                typeof (child as any).getColumnMethods === "function"
        ) as Column[];
        this.tableColumnsMethods = await Promise.all(
            this.tableColumns.map((col) => (col as any).getColumnMethods())
        );
    }

    private getDataWithInternalIds(): TableRowWithId[] | undefined {
        return this.data?.map((row, i) => ({
            ...row,
            _uicTableInternalId: `${i}`,
        }));
    }

    /**
     * triggered when a row was selected
     */
    @Event({
        eventName: "uicRowsSelected",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    rowsSelected: EventEmitter;

    /**
     * triggered when a row was clicked
     */
    @Event({
        eventName: "uicRowClick",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    rowClick: EventEmitter;

    @Watch("data")
    onDataChanged() {
        const rows = this.getDataWithInternalIds();
        if (rows === undefined) return;

        this.sortedData = this.sortRowsByInitialCriterion(rows);
        this.setSelectedRows(this.sortedData);
    }

    private setSelectedRows(rows: TableRowWithId[]) {
        this.selectedRows = rows
            .filter((tableRow) => {
                return tableRow.selected;
            })
            .map((tableRow) => tableRow._uicTableInternalId);
    }

    @Watch("selectedRows")
    emitSelectedRows() {
        this.rowsSelected.emit({
            selectedRows: this.sortedData.filter((row: TableRowWithId) =>
                this.selectedRows.includes(row._uicTableInternalId)
            ),
        });
    }

    /**
     * Triggers sorting of defined columns if `self-sorting` is enabled
     */
    private sortRowsByInitialCriterion(rows: TableRowWithId[]) {
        if (!this.selfSorting || this.initialSortingCriterion === undefined)
            return rows;

        const index = this.tableColumns.findIndex(
            (column) =>
                this.initialSortingCriterion.sortProperty ===
                    column.sortField ||
                this.initialSortingCriterion.sortProperty === column.field
        );
        if (index !== -1) {
            return this.sort(
                index,
                this.initialSortingCriterion.direction,
                rows
            );
        } else {
            return rows;
        }
    }

    /**
     * Listen to table sorting events
     */
    @Listen("uicTableSort")
    onSortRow(e: CustomEvent) {
        if (!this.selfSorting) return;
        const sorter = e.target as HTMLUicTableHeadSortElement;
        const th = sorter.closest("th");
        const { sortedDirection } = sorter;
        const titleRow = th.parentElement;
        const colIndex = [].indexOf.call(titleRow.children, th);
        this.sortedData = this.sort(colIndex, sortedDirection, this.sortedData);
    }

    private sort(
        columnIndex: number,
        direction: SortDirection,
        rows: TableRowWithId[]
    ) {
        const getSortString =
            this.tableColumnsMethods[columnIndex].getSortString || (() => "");

        if (direction === "") {
            return this.getDataWithInternalIds();
        }

        const comparer = (a: TableRow, b: TableRow) => {
            const strA = getSortString(a);
            const strB = getSortString(b);
            return direction === "asc"
                ? strA.localeCompare(strB)
                : strB.localeCompare(strA);
        };
        return rows.sort(comparer);
    }

    /** render lazy start */
    @State() firstRowHeight: number = 0;
    @State() lastRowHeight: number = 0;
    @State() step: number = 0;
    @State() startIndex: number = 0;

    private tbody: HTMLElement = null;
    private renderCoeff: number = 2;

    private initTable = () => {
        const tableTop = this.tbody.getBoundingClientRect().top;
        const viewPortY = document.documentElement.clientHeight;

        if (tableTop > 0) {
            this.step = Math.floor(
                (viewPortY * this.renderCoeff - tableTop) / this.lazyRowHeight
            );
        } else {
            this.step = Math.floor(
                (viewPortY * this.renderCoeff) / this.lazyRowHeight
            );
            this.startIndex = Math.floor(-tableTop / this.lazyRowHeight);
        }
        this.firstRowHeight = this.startIndex * this.lazyRowHeight;
        this.lastRowHeight =
            this.data.length * this.lazyRowHeight -
            this.step * this.lazyRowHeight;
    };

    private getTopDistance(el1: HTMLElement, el2: HTMLElement | Window) {
        const el1RectTop = el1.getBoundingClientRect().top;
        const el2RectTop =
            el2 === window
                ? 0
                : (el2 as HTMLElement).getBoundingClientRect().top!;
        return el1RectTop - el2RectTop;
    }

    private onScroll = () => {
        const top = this.getTopDistance(
            this.tbody,
            this.scrollEventTargetElement
        );
        const viewportY = document.documentElement.clientHeight;

        if (top < 0) {
            this.step = Math.floor(
                (viewportY * this.renderCoeff) / this.lazyRowHeight
            );
            this.startIndex = Math.min(
                this.data.length - this.step > 1
                    ? this.data.length - this.step
                    : 1,
                Math.floor(-top / this.lazyRowHeight)
            );
        } else {
            this.startIndex = 0;
            this.step = Math.floor(
                (viewportY * this.renderCoeff - top) / this.lazyRowHeight
            );
        }
    };

    @Watch("startIndex")
    updateHeights() {
        this.firstRowHeight = this.startIndex * this.lazyRowHeight;
        this.lastRowHeight = Math.floor(
            this.data.length * this.lazyRowHeight -
                this.step * this.lazyRowHeight -
                this.firstRowHeight
        );
    }

    private scroller: HTMLDivElement = null;

    /** Only to be used when the lazy-render property is set.
     *  call this method to scroll to an elemement that is not yet rendered.
     **/
    @Method()
    async scrollToVirtualElement(
        name: string | number,
        value: string | number,
        behavior: "auto" | "smooth" = "smooth"
    ) {
        if (!this.lazyRender) return;
        let elementIndex = 0;
        this.sortedData.find((element, index) => {
            if (element[name] == value) elementIndex = index;
            return element[name] == value;
        });
        this.scroller.style.top =
            this.thead.clientHeight +
            elementIndex * this.lazyRowHeight +
            (this.stickyThead ? 67 : 0) +
            "px";
        if (this.scrollEventTargetElement === window) {
            this.scroller.scrollIntoView({
                behavior: behavior,
            });
        } else {
            const innerTop = this.tableInner.offsetTop;
            const scroller = this.scroller.offsetTop;
            this.tableInner.scrollTo({
                top: scroller - innerTop - (this.stickyThead ? 67 : 0) * 2,
                behavior: behavior,
            });
        }
    }

    render() {
        const {
            fullWidth,
            baseClass,
            selectedRows,
            sortedData,
            stickyThead,
        } = this;
        const innerScroll = stickyThead ? () => this.onScroll() : null;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--full-width`]: fullWidth,
                    [`${baseClass}--sticky-thead`]: stickyThead,
                }}
            >
                <slot />
                {this.lazyRender && (
                    <div
                        class={`${baseClass}__scroller`}
                        ref={(el) => (this.scroller = el)}
                    ></div>
                )}
                <div
                    class={`${baseClass}__inner`}
                    onScroll={innerScroll}
                    ref={(el) => (this.tableInner = el)}
                >
                    <table class={`${baseClass}__table`}>
                        <thead
                            class={`${baseClass}__thead`}
                            ref={(el) => (this.thead = el)}
                        >
                            <tr class={`${baseClass}__tr-head`}>
                                {this.tableColumnsMethods?.map((renderer) =>
                                    renderer.renderHeader(this)
                                )}
                            </tr>
                        </thead>
                        <tbody
                            ref={(el) => (this.tbody = el)}
                            class={`${baseClass}__tbody`}
                        >
                            {this.lazyRender && (
                                <tr
                                    style={{
                                        height: `${this.firstRowHeight}px`,
                                        maxHeight: `${this.firstRowHeight}px`,
                                    }}
                                ></tr>
                            )}
                            {this.lazyRender &&
                                sortedData
                                    .slice(
                                        this.startIndex,
                                        this.startIndex + this.step
                                    )
                                    .map((tableRow: TableRowWithId) => {
                                        return (
                                            <tr
                                                class={{
                                                    ["uic-table__tr"]: true,
                                                    ["uic-table__tr--selected"]: selectedRows.includes(
                                                        tableRow._uicTableInternalId
                                                    ),
                                                }}
                                                onClick={(e) => {
                                                    this.rowClick.emit({
                                                        row: tableRow,
                                                        clickEvent: e,
                                                    });
                                                }}
                                                key={
                                                    tableRow._uicTableInternalId
                                                }
                                            >
                                                {this.tableColumnsMethods?.map(
                                                    (renderer) => {
                                                        return renderer.renderCell(
                                                            tableRow,
                                                            this
                                                        );
                                                    }
                                                )}
                                            </tr>
                                        );
                                    })}
                            {this.lazyRender && (
                                <tr
                                    style={{
                                        height: `${this.lastRowHeight}px`,
                                        maxHeight: `${this.lastRowHeight}px`,
                                    }}
                                ></tr>
                            )}
                            {!this.lazyRender &&
                                sortedData?.map((tableRow: TableRowWithId) => (
                                    <tr
                                        class={{
                                            ["uic-table__tr"]: true,
                                            ["uic-table__tr--selected"]: selectedRows.includes(
                                                tableRow._uicTableInternalId
                                            ),
                                        }}
                                        onClick={(e) => {
                                            this.rowClick.emit({
                                                row: tableRow,
                                                clickEvent: e,
                                            });
                                        }}
                                    >
                                        {this.tableColumnsMethods?.map(
                                            (renderer) => {
                                                return renderer.renderCell(
                                                    tableRow,
                                                    this
                                                );
                                            }
                                        )}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </Host>
        );
    }
}
